import React, { useState } from 'react';
import { X, Loader2, Trophy, MapPin, Briefcase, Target, Star, Clipboard, ListChecks, Linkedin, FileText, ChevronDown, ChevronUp, Zap, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ScoreBreakdown } from '../ScoreBreakdown';
import { API_URL, CANDIDATE_STAGES } from './constants';

const RECOMMENDATION_COLORS = {
  strong_recommend: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  recommend: 'bg-blue-100 text-blue-700 border-blue-300',
  conditional: 'bg-amber-100 text-amber-700 border-amber-300',
  weak: 'bg-red-100 text-red-700 border-red-300',
};

export const CandidateComparison = ({ roleId, roleTitle, company, selectedCandidates, onClose, onCandidateUpdate, onGeneratePitch }) => {
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  const runComparison = async () => {
    if (selectedCandidates.length < 2) { toast.error('Select at least 2 candidates'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/roles/${roleId}/compare-candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidate_keys: selectedCandidates.map(c => c.candidate_key) }),
      });
      const data = await res.json();
      if (data.error) { toast.error(data.detail); }
      else { setComparison(data); }
    } catch { toast.error('Comparison failed'); }
    setLoading(false);
  };

  const handleRecommendForSubmission = async () => {
    if (!comparison) return;
    const best = comparison.candidates.find(c => c.candidate_key === comparison.best_fit?.overall);
    if (!best) { toast.info('No best fit determined'); return; }
    try {
      const token = localStorage.getItem('backoffice_token');
      // Add to submission
      await fetch(`${API_URL}/api/intelligence/acquisition/candidates/${encodeURIComponent(best.candidate_key)}/submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ add: true }),
      });
      // Move to submitted stage
      await fetch(`${API_URL}/api/intelligence/acquisition/candidates/${encodeURIComponent(best.candidate_key)}/stage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stage: 'submitted' }),
      });
      // Copy summary
      navigator.clipboard.writeText(comparison.client_summary);
      toast.success(`${best.name} added to submission list + summary copied to clipboard`);
      if (onCandidateUpdate) onCandidateUpdate();
    } catch { toast.error('Failed to submit recommendation'); }
  };

  const copySummary = () => {
    if (comparison?.client_summary) {
      navigator.clipboard.writeText(comparison.client_summary);
      toast.success('Client-ready summary copied to clipboard');
    }
  };

  // Pre-comparison: trigger view
  if (!comparison) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="comparison-pre-modal">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-gradient-to-br from-[#0A1F44] to-[#1a3a6b] rounded-lg flex items-center justify-center">
                <Target className="h-4 w-4 text-[#D4AF37]" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#0A1F44]">Compare Candidates</h2>
                <p className="text-[10px] text-gray-500">{roleTitle} at {company}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
          <div className="space-y-2 mb-4">
            {selectedCandidates.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center ${c.fit_score >= 50 ? 'bg-emerald-100' : c.fit_score >= 30 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <span className={`text-[10px] font-bold ${c.fit_score >= 50 ? 'text-emerald-700' : c.fit_score >= 30 ? 'text-amber-700' : 'text-gray-600'}`}>{c.fit_score}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#0A1F44]">{c.name}</span>
                  <p className="text-[10px] text-gray-500">{c.title} at {c.current_company}</p>
                </div>
              </div>
            ))}
          </div>
          <button onClick={runComparison} disabled={loading || selectedCandidates.length < 2}
            className="w-full px-4 py-2.5 bg-[#D4AF37] text-[#0A1F44] text-sm font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="run-comparison-btn">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing with AI...</> : <><Target className="h-4 w-4" /> Run AI Comparison</>}
          </button>
        </div>
      </div>
    );
  }

  // Post-comparison: full view
  const { candidates, best_fit, ai_analysis, client_summary } = comparison;
  const aiCandidates = ai_analysis?.candidates || [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="comparison-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-gradient-to-br from-[#0A1F44] to-[#1a3a6b] rounded-lg flex items-center justify-center">
              <Target className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0A1F44]">Candidate Comparison</h2>
              <p className="text-xs text-gray-500">{roleTitle} at {company} — {candidates.length} candidates</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRecommendForSubmission}
              className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 flex items-center gap-1.5"
              data-testid="recommend-submit-btn">
              <Zap className="h-3.5 w-3.5" /> Recommend for Submission
            </button>
            {onGeneratePitch && (
              <button onClick={() => onGeneratePitch(
                selectedCandidates.map(c => c.candidate_key),
                selectedCandidates.map(c => c.name)
              )}
                className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] flex items-center gap-1.5"
                data-testid="comparison-generate-pitch-btn">
                <FileText className="h-3.5 w-3.5" /> Generate Pitch
              </button>
            )}
            <button onClick={copySummary}
              className="px-3 py-2 bg-[#0A1F44] text-white text-xs font-bold rounded-lg hover:bg-[#0d2a5c] flex items-center gap-1.5"
              data-testid="copy-comparison-summary-btn">
              <FileText className="h-3.5 w-3.5" /> Copy Client Summary
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-400" /></button>
          </div>
        </div>

        {/* AI Winner Banner */}
        {ai_analysis?.overall_winner && (
          <div className="px-6 py-3 bg-[#D4AF37]/10 border-b border-[#D4AF37]/20 flex items-center gap-2" data-testid="ai-winner-banner">
            <Trophy className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-sm font-bold text-[#0A1F44]">AI Recommendation: {ai_analysis.overall_winner}</span>
            <span className="text-xs text-gray-600">— {ai_analysis.winner_reason}</span>
          </div>
        )}

        {/* Comparison Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className={`grid gap-4 ${candidates.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {candidates.map((c, ci) => {
              const isBestOverall = c.candidate_key === best_fit?.overall;
              const isBestTitle = c.candidate_key === best_fit?.best_title_match;
              const isBestLocation = c.candidate_key === best_fit?.best_location;
              const isBestSeniority = c.candidate_key === best_fit?.best_seniority;
              const aiData = aiCandidates.find(a => a.name === c.name) || {};
              const recColor = RECOMMENDATION_COLORS[aiData.recommendation] || 'bg-gray-100 text-gray-600';

              return (
                <div key={ci} className={`border rounded-xl overflow-hidden transition-all ${isBestOverall ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/30 shadow-lg' : 'border-gray-200'}`}
                  data-testid={`comparison-card-${ci}`}>
                  {/* Candidate Header */}
                  <div className={`p-4 ${isBestOverall ? 'bg-[#D4AF37]/10' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ring-2 ${c.fit_score >= 50 ? 'bg-emerald-100 ring-emerald-300' : c.fit_score >= 30 ? 'bg-amber-100 ring-amber-300' : 'bg-gray-100 ring-gray-300'}`}>
                          <span className={`text-sm font-bold ${c.fit_score >= 50 ? 'text-emerald-700' : c.fit_score >= 30 ? 'text-amber-700' : 'text-gray-600'}`}>{c.fit_score}</span>
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-[#0A1F44]">{c.name}</h3>
                          {c.sourcing_pass === 'pass_2' && <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[7px] font-bold rounded">Expansion</span>}
                        </div>
                      </div>
                      {isBestOverall && (
                        <span className="px-2 py-1 bg-[#D4AF37] text-[#0A1F44] text-[9px] font-bold rounded-lg flex items-center gap-1" data-testid={`best-fit-badge-${ci}`}>
                          <Trophy className="h-3 w-3" /> BEST FIT
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Briefcase className="h-3 w-3 text-gray-400" /> {c.title} at {c.current_company}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <MapPin className="h-3 w-3 text-gray-400" /> {c.location || 'Unknown'}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Star className="h-3 w-3 text-gray-400" /> Seniority: <span className="font-medium capitalize">{c.seniority}</span>
                      </div>
                    </div>
                  </div>

                  {/* Category Badges */}
                  <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap gap-1">
                    {isBestTitle && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[8px] font-bold rounded" data-testid={`badge-title-${ci}`}>Best Title Match</span>}
                    {isBestLocation && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[8px] font-bold rounded" data-testid={`badge-location-${ci}`}>Best Location</span>}
                    {isBestSeniority && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-bold rounded" data-testid={`badge-seniority-${ci}`}>Best Seniority</span>}
                    {aiData.recommendation && <span className={`px-2 py-0.5 text-[8px] font-bold rounded border ${recColor}`} data-testid={`recommendation-${ci}`}>{aiData.recommendation?.replace(/_/g, ' ').toUpperCase()}</span>}
                  </div>

                  {/* Score Breakdown */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-[9px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">Score Breakdown</p>
                    <ScoreBreakdown breakdown={c.score_breakdown} totalScore={c.fit_score} />
                  </div>

                  {/* AI Analysis */}
                  {(aiData.why_strong || aiData.concerns) && (
                    <div className="px-4 py-3 border-b border-gray-100 space-y-2">
                      <p className="text-[9px] text-gray-500 uppercase tracking-wider font-medium">AI Decision Assist</p>
                      {aiData.why_strong && (
                        <div className="flex items-start gap-1.5">
                          <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <p className="text-[11px] text-gray-700 leading-relaxed">{aiData.why_strong}</p>
                        </div>
                      )}
                      {aiData.concerns && (
                        <div className="flex items-start gap-1.5">
                          <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-[11px] text-gray-700 leading-relaxed">{aiData.concerns}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Match Factors & Gaps */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    {(c.fit_reasons || []).length > 0 && (
                      <div className="mb-2">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wider font-medium mb-1">Key Match Factors</p>
                        <div className="flex flex-wrap gap-1">
                          {c.fit_reasons.filter(r => !r.startsWith('keyword_bonus')).slice(0, 4).map((r, ri) => (
                            <span key={ri} className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-[9px] rounded">{r.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(c.mismatch_reasons || []).length > 0 && (
                      <div>
                        <p className="text-[9px] text-gray-500 uppercase tracking-wider font-medium mb-1">Gaps / Weaknesses</p>
                        <div className="flex flex-wrap gap-1">
                          {c.mismatch_reasons.slice(0, 3).map((m, mi) => (
                            <span key={mi} className="px-1.5 py-0.5 bg-red-50 text-red-600 text-[9px] rounded">{m}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="px-4 py-3 flex flex-wrap gap-1.5" data-testid={`comparison-actions-${ci}`}>
                    <button onClick={async () => {
                      const token = localStorage.getItem('backoffice_token');
                      await fetch(`${API_URL}/api/intelligence/acquisition/candidates/${encodeURIComponent(c.candidate_key)}/submission`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ add: true }),
                      });
                      toast.success(`${c.name} added to submission list`);
                      if (onCandidateUpdate) onCandidateUpdate();
                    }} className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-[9px] font-bold rounded hover:bg-emerald-100"
                      data-testid={`compare-submit-${ci}`}>
                      <ListChecks className="h-3 w-3" /> Add to Submission
                    </button>
                    <button onClick={() => {
                      const parts = [c.name, c.title, c.current_company, c.location].filter(Boolean);
                      navigator.clipboard.writeText(parts.join(' | '));
                      toast.success('Copied');
                    }} className="flex items-center gap-1 px-2 py-1 bg-gray-50 text-gray-600 text-[9px] font-bold rounded hover:bg-gray-100"
                      data-testid={`compare-copy-${ci}`}>
                      <Clipboard className="h-3 w-3" /> Copy Summary
                    </button>
                    {c.has_linkedin && (
                      <button onClick={() => window.open(c.linkedin_url, '_blank')}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-[9px] font-bold rounded hover:bg-blue-100"
                        data-testid={`compare-linkedin-${ci}`}>
                        <Linkedin className="h-3 w-3" /> LinkedIn
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Client-Ready Summary Preview */}
          {client_summary && (
            <div className="mt-6">
              <button onClick={() => setShowSummary(s => !s)}
                className="flex items-center gap-2 text-sm font-bold text-[#0A1F44] hover:text-[#D4AF37] transition-colors"
                data-testid="toggle-summary-preview">
                {showSummary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                <FileText className="h-4 w-4" /> Client-Ready Summary Preview
              </button>
              {showSummary && (
                <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg font-mono" data-testid="summary-preview">
                  <pre className="text-[11px] text-[#0A1F44] whitespace-pre-wrap leading-relaxed">{client_summary}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
